import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAdminApi } from '../hooks/useAdminApi';
import {
  ArrowLeft, Truck, Container, Save, Loader2, Gauge, Calendar, Printer
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import AxleDiagram from '../../components/AxleDiagram';

const BrakeTireMeasurementPage = () => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoprint = searchParams.get('autoprint');
  const { get: adminGet, post: adminPost } = useAdminApi();

  // Support atelier indépendant: fallback to mechanic_token
  const mechToken = typeof window !== 'undefined' ? localStorage.getItem('mechanic_token') : null;
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const isAtelier = !adminToken && !!mechToken;

  const get = useCallback(async (endpoint) => {
    const res = await adminGet(endpoint);
    if (res.success) return res;
    if (isAtelier) {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${endpoint}`, {
          headers: { 'Authorization': `Bearer ${mechToken}` }
        });
        const data = await r.json();
        return { success: r.ok, data: Array.isArray(data) ? data : data };
      } catch { return { success: false }; }
    }
    return res;
  }, [adminGet, isAtelier, mechToken]);

  const post = useCallback(async (endpoint, body) => {
    const res = await adminPost(endpoint, body);
    if (res.success) return res;
    if (isAtelier) {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${endpoint}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${mechToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        const data = await r.json();
        return { success: r.ok, data };
      } catch { return { success: false }; }
    }
    return res;
  }, [adminPost, isAtelier, mechToken]);

  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [measurementDate, setMeasurementDate] = useState(new Date().toISOString().split('T')[0]);
  const [odometer, setOdometer] = useState('');
  const [brakeUnit, setBrakeUnit] = useState('mm');
  const [tireUnit, setTireUnit] = useState('mm');
  const [notes, setNotes] = useState('');
  const [inspectorName, setInspectorName] = useState('');
  const [axles, setAxles] = useState([]);

  const addAxle = useCallback((hasDual = true) => {
    setAxles(prev => [...prev, {
      axle_number: prev.length + 1,
      has_dual_tires: hasDual,
      left_brake: null, left_tire_inner: null, left_tire_outer: null,
      right_brake: null, right_tire_inner: null, right_tire_outer: null,
    }]);
  }, []);

  const removeAxle = () => {
    if (axles.length <= 1) return;
    setAxles(prev => prev.slice(0, -1));
  };

  const updateAxle = (idx, field, value) => {
    setAxles(prev => prev.map((a, i) => i === idx ? { ...a, [field]: value } : a));
  };

  const toggleDualTires = (idx) => {
    setAxles(prev => prev.map((a, i) => i === idx ? {
      ...a,
      has_dual_tires: !a.has_dual_tires,
      left_tire_outer: !a.has_dual_tires ? a.left_tire_outer : null,
      right_tire_outer: !a.has_dual_tires ? a.right_tire_outer : null,
    } : a));
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const res = await get('/saas/fleet/vehicles');
      let v = null;
      if (res.success) {
        v = res.data.find(v => v.id === vehicleId);
      }
      // Also search atelier vehicles if not found
      if (!v) {
        const atelierRes = await get('/saas/fleet/vehicles?include_atelier=true');
        if (atelierRes.success) {
          v = atelierRes.data.find(v => v.id === vehicleId);
        }
      }
      if (res.success || v) {
        if (v) {
          setVehicle(v);
          setOdometer(v.current_odometer?.toString() || '');

          // Try to load latest measurements for this vehicle
          const measRes = await get(`/saas/fleet/vehicles/${vehicleId}/measurements`);
          let loaded = false;
          if (measRes.success && measRes.data?.length > 0) {
            // Find first record with actual data
            const latest = measRes.data.find(m => {
              const ax = m.axles || [];
              return ax.some(a =>
                a.left_brake != null || a.right_brake != null ||
                a.left_tire_inner != null || a.left_tire_outer != null ||
                a.right_tire_inner != null || a.right_tire_outer != null
              );
            });
            if (latest && latest.axles?.length > 0) {
              setAxles(latest.axles.map(a => ({
                axle_number: a.axle_number || 1,
                has_dual_tires: a.has_dual_tires !== false,
                left_brake: a.left_brake,
                left_tire_inner: a.left_tire_inner,
                left_tire_outer: a.left_tire_outer,
                right_brake: a.right_brake,
                right_tire_inner: a.right_tire_inner,
                right_tire_outer: a.right_tire_outer,
              })));
              if (latest.measurement_date) setMeasurementDate(latest.measurement_date);
              if (latest.odometer) setOdometer(latest.odometer.toString());
              if (latest.brake_unit) setBrakeUnit(latest.brake_unit);
              if (latest.tire_unit) setTireUnit(latest.tire_unit);
              if (latest.notes) setNotes(latest.notes);
              if (latest.inspector_name || latest.technician_name) setInspectorName(latest.inspector_name || latest.technician_name);
              loaded = true;
            }
          }

          if (!loaded) {
            const isTruck = v.vehicle_type === 'truck';
            const mkAxle = (n, dual) => ({ axle_number: n, has_dual_tires: dual, left_brake: null, left_tire_inner: null, left_tire_outer: null, right_brake: null, right_tire_inner: null, right_tire_outer: null });
            setAxles(isTruck
              ? [mkAxle(1, false), mkAxle(2, true), mkAxle(3, true)]
              : [mkAxle(1, true), mkAxle(2, true), mkAxle(3, true)]
            );
          }
        } else {
          toast.error('Vehicule non trouve');
          navigate(-1);
        }
      }
      setLoading(false);
    };
    load();
  }, [get, vehicleId, navigate]);

  // Auto-print: navigate from fleet card with ?autoprint=classic triggers print
  useEffect(() => {
    if (autoprint !== 'classic' || loading) return;
    const timer = setTimeout(() => {
      const btn = document.querySelector('[data-testid="classic-measure-print-btn"]');
      if (btn) btn.click();
    }, 600);
    return () => clearTimeout(timer);
  }, [autoprint, loading]);

  const handleSubmit = async () => {
    if (axles.length === 0) { toast.error('Ajoutez au moins un essieu'); return; }

    setSubmitting(true);
    const result = await post(`/saas/fleet/vehicles/${vehicleId}/measurements`, {
      vehicle_id: vehicleId,
      measurement_date: measurementDate,
      odometer: parseInt(odometer) || 0,
      brake_unit: brakeUnit,
      tire_unit: tireUnit,
      axles,
      notes,
      inspector_name: inspectorName,
    });

    if (result.success) {
      const mId = result.data?.measurement?.id;
      if (mId) {
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('admin_token');
        try {
          const pdfResp = await fetch(`${apiUrl}/api/saas/fleet/measurements/${mId}/pdf`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (pdfResp.ok) {
            const blob = await pdfResp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Mesures_Freins_Pneus_${vehicle.unit_number}_${measurementDate}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }
        } catch { /* PDF is bonus */ }
      }
      toast.success('Mesures enregistrees — PDF telecharge');
      navigate(-1);
    } else {
      toast.error(result.error || 'Erreur');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-amber-500" size={32} /></div>;
  if (!vehicle) return null;

  const unitLabel = (unit) => ({ mm: 'mm', pouces: 'po', '32emes': '/32', pourcent: '%' }[unit] || unit);

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-5xl mx-auto">
      <Link to="/admin/fleet" className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-500 transition-colors">
        <ArrowLeft size={18} /> Retour a la flotte
      </Link>

      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
            {vehicle.vehicle_type === 'truck' ? <Truck className="text-slate-700" size={24} /> : <Container className="text-slate-700" size={24} />}
          </div>
          <div>
            <h1 className="text-xl font-bold text-black" data-testid="measurement-title">
              Mesures Freins & Pneus — {vehicle.unit_number}
            </h1>
            <p className="text-slate-500 text-sm">
              {vehicle.brand} {vehicle.model} {vehicle.year}
              {vehicle.pnbv ? ` · PNBV: ${vehicle.pnbv.toLocaleString()} kg` : ''}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1"><Calendar size={11} className="inline mr-1" />Date</label>
            <Input type="date" value={measurementDate} onChange={e => setMeasurementDate(e.target.value)}
              className="bg-white border-slate-300 text-black" data-testid="input-date" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1"><Gauge size={11} className="inline mr-1" />Odometre (km)</label>
            <Input type="number" value={odometer} onChange={e => setOdometer(e.target.value)}
              className="bg-white border-slate-300 text-black" data-testid="input-odometer" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Technicien</label>
            <Input type="text" value={inspectorName} onChange={e => setInspectorName(e.target.value)}
              placeholder="Nom du technicien"
              className="bg-white border-slate-300 text-black" data-testid="input-technician" />
          </div>
        </div>
      </div>

      {/* Format Toggle */}
      <AxleDiagram
            axles={axles}
            brakeUnit={brakeUnit}
            tireUnit={tireUnit}
            onUpdateAxle={updateAxle}
            onToggleDual={toggleDualTires}
            onAddAxle={addAxle}
            onRemoveAxle={removeAxle}
            onBrakeUnitChange={setBrakeUnit}
            onTireUnitChange={setTireUnit}
            dark={false}
          />

      {/* Notes (utilise en mode Standard seulement - mode Classic a sa propre zone notes) */}
      {true && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm notes-print-wrapper">
          <label className="block text-sm text-slate-600 font-medium mb-2">Notes</label>
          <textarea
            value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Observations, remarques..."
            className="notes-textarea w-full border border-slate-200 rounded-lg py-2 px-3 text-black bg-white focus:outline-none focus:border-amber-500 min-h-[96px] resize-y"
            rows={4}
            data-testid="input-notes"
          />
          <pre className="notes-print-mirror hidden print:block text-black text-sm whitespace-pre-wrap break-words font-sans mt-0">{notes}</pre>
        </div>
      )}

      {/* Submit */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-center justify-between sticky bottom-4">
        <p className="text-sm text-slate-500">{axles.length} essieu(x) · Freins: {unitLabel(brakeUnit)} · Pneus: {unitLabel(tireUnit)}</p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate(-1)} className="border-slate-300 text-slate-600">Annuler</Button>
          <Button variant="outline" onClick={() => window.print()} className="print-btn border-slate-300 text-slate-600" data-testid="print-measurements-btn">
            <Printer size={16} className="mr-2" />
            Imprimer
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} className="bg-black hover:bg-slate-800 text-white min-w-[200px]" data-testid="submit-measurements-btn">
            {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BrakeTireMeasurementPage;
